import React, { useState, useRef } from "react";
import TopBarHeader from "./TopBarHeader";
import icon from "../assets/icon-upload.png";
import "../style/documentosSpliter.css";
import eyeIcon from '../assets/eye-icon.svg'; // Import the eye icon
import trashIcon from '../assets/trash-icon.svg'; // Import the trash icon

// Función para normalizar nombres de archivo
const normalizeFileName = (fileName) => {
  return fileName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9.-]/g, "_")
    .toLowerCase();
};

const SpliterDocumental = () => {
  const [documentos, setDocumentos] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success"
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      
      // Validar archivos antes de procesarlos
      files.forEach(file => {
        // Validar tipo de archivo
        const typeValidation = validateFileType(file);
        if (!typeValidation.valid) {
          showNotification(typeValidation.error, "error");
          return;
        }
        
        // Validar tamaño de archivo
        const sizeValidation = validateFileSize(file);
        if (!sizeValidation.valid) {
          showNotification(sizeValidation.error, "error");
          return;
        }
        
        // Procesar cada archivo individualmente
        updateDocumentos(file);
      });
    }
  };

  const handleButtonClick = () => {
    inputRef.current.click();
  };
  
  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      
      // Verificar que los archivos sean de tipos permitidos y tamaño adecuado
      files.forEach(file => {
        // Validar tipo de archivo
        const typeValidation = validateFileType(file);
        if (!typeValidation.valid) {
          showNotification(typeValidation.error, "error");
          return;
        }
        
        // Validar tamaño de archivo
        const sizeValidation = validateFileSize(file);
        if (!sizeValidation.valid) {
          showNotification(sizeValidation.error, "error");
          return;
        }
        
        // Si pasa todas las validaciones, procesar el archivo
        updateDocumentos(file);
      });
    }
  };
  
  const updateDocumentos = async (file) => {
    // Normalizar nombre del archivo para eliminar tildes y caracteres especiales
    const normalizedFileName = normalizeFileName(file.name);
    
    // Crear nuevo documento con estado de carga
    const newDoc = {
      id: Date.now() + Math.random(), // ID único para cada documento
      // tipo: TIPOS_DOCUMENTO[0], // Eliminar esta línea
      nombre: file.name, // Mantener nombre original para mostrar en la UI
      nombreNormalizado: normalizedFileName, // Nombre normalizado para enviar
      archivo: file,
      status: 'uploading', // Indicar que está cargando
      gsutilUri: null // Inicializar el campo gsutilUri
    };
    
    // Actualizar lista de documentos
    setDocumentos(prevDocs => [...prevDocs, newDoc]);
    
    // Crear un FormData para enviar el archivo
    const formData = new FormData();
    
    // Agregar el archivo con el nombre 'file' como espera la Cloud Function
    formData.append('file', file, file.name);
    
    try {
      // URL de tu Cloud Function para el splitter documental
      const cloudFunctionUrl = "https://labs-spliter-126625280314.us-central1.run.app/uploadPDF";
      
      // Enviar el archivo a la Cloud Function
      const response = await fetch(cloudFunctionUrl, {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (response.ok) {
        console.log('Respuesta del servidor:', result);
        
        // Actualizar el documento con el resultado exitoso
        setDocumentos(prevDocs => 
          prevDocs.map(doc => 
            doc.id === newDoc.id 
              ? { 
                  ...doc, 
                  status: 'success',
                  analysis: result,
                  gsutilUri: result.files?.[0]?.gsUrl || null
                }
              : doc
          )
        );
        
        // Obtener el nombre del archivo para contar páginas
        let fileName = null;
        
        // La respuesta tiene estructura: { files: [{ filename: "...", gsUrl: "..." }] }
        if (result.files && result.files.length > 0) {
          // Usar el filename que es el nombre real en el bucket
          fileName = result.files[0].filename;
        } else if (result.gsutilUri) {
          // Fallback: extraer de gsutilUri si existe
          fileName = result.gsutilUri.split('/').pop();
        } else if (result.fileName) {
          // Otro fallback
          fileName = result.fileName;
        } else {
          // Último fallback: usar el nombre normalizado
          fileName = normalizedFileName;
        }
        
        if (fileName) {
          try {
            console.log('Enviando nombre de archivo para contar páginas:', fileName);
            
            // Llamar a la función para contar páginas
            const pageCountResponse = await fetch('https://labs-spliter-download-126625280314.us-central1.run.app/countPdfPages', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                fileName: fileName
              }),
            });
            
            const pageCountResult = await pageCountResponse.json();
            
            if (pageCountResponse.ok) {
              console.log('Información de páginas:', pageCountResult);
              
              // Actualizar el documento con la información de páginas
              setDocumentos(prevDocs => 
                prevDocs.map(doc => 
                  doc.id === newDoc.id 
                    ? { 
                        ...doc, 
                        pageCount: pageCountResult.pageCount,
                        // Campos para páginas separadas
                        hasSeparatedPages: pageCountResult.hasSeparatedPages,
                        separatedPages: pageCountResult.separatedPages,
                        zipDownloadUrl: pageCountResult.zipDownloadUrl
                      }
                    : doc
                )
              );
              
              // Mostrar notificación con información de separación
              if (pageCountResult.hasSeparatedPages) {
                showNotification(
                  `Archivo procesado exitosamente. ${pageCountResult.pageCount} páginas separadas y comprimidas.`, 
                  'success'
                );
                
                // Iniciar descarga automática del ZIP
                if (pageCountResult.zipDownloadUrl) {
                  console.log('Iniciando descarga automática del ZIP...');
                  downloadFile(pageCountResult.zipDownloadUrl, `${file.name.replace('.pdf', '')}_paginas_separadas.zip`);
                }
              } else {
                showNotification(`Archivo procesado exitosamente. ${pageCountResult.pageCount} página${pageCountResult.pageCount !== 1 ? 's' : ''}.`, 'success');
              }
            } else {
              console.error('Error al contar páginas:', pageCountResult);
              showNotification(`Archivo procesado pero no se pudo contar páginas: ${pageCountResult.error}`, 'warning');
            }
          } catch (pageCountError) {
            console.error('Error al conectar con el servicio de conteo de páginas:', pageCountError);
            showNotification('Archivo procesado pero no se pudo contar páginas', 'warning');
          }
        } else {
          console.warn('No se pudo extraer el nombre del archivo de la respuesta');
          showNotification('Archivo procesado exitosamente', 'success');
        }
      } else {
        console.error('Error del servidor:', result);
        
        // Actualizar el documento con estado de error
        setDocumentos(prevDocs => 
          prevDocs.map(doc => 
            doc.id === newDoc.id 
              ? { 
                  ...doc, 
                  status: 'error',
                  error: result.error || 'Error desconocido'
                }
              : doc
          )
        );
        
        showNotification(`Error al procesar archivo: ${result.error || 'Error desconocido'}`, 'error');
      }
    } catch (error) {
      console.error('Error al enviar archivo:', error);
      
      // Actualizar el documento con estado de error
      setDocumentos(prevDocs => 
        prevDocs.map(doc => 
          doc.id === newDoc.id 
            ? { 
                ...doc, 
                status: 'error',
                error: 'Error de conexión'
              }
            : doc
        )
      );
      
      showNotification('Error de conexión al procesar archivo', 'error');
    }
  };

  // Función para mostrar notificaciones
  const showNotification = (message, type) => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Ocultar la notificación después de 5 segundos
    setTimeout(() => {
      setNotification(prev => ({...prev, show: false}));
    }, 5000);
  };

  // Función para validar el tamaño máximo de archivo (20MB)
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB en bytes

  const validateFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `El archivo excede el tamaño máximo permitido (20MB). Tamaño actual: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
      };
    }
    return { valid: true };
  };

  // Función para validar tipo de archivo (solo PDF)
  const validateFileType = (file) => {
    const allowedTypes = ['application/pdf'];
    
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Tipo de archivo no permitido: ${file.type}. Solo se permiten archivos PDF.`
      };
    }
    
    // Validación adicional para detectar posibles archivos corruptos o malformados
    if (file.size === 0) {
      return {
        valid: false,
        error: 'El archivo está vacío y no puede ser procesado.'
      };
    }
    
    return { valid: true };
  };

  // Función para determinar el tipo de archivo
  const getFileTypeInfo = (file) => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type;
    
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return { type: 'pdf', label: 'PDF', className: 'pdf-label' };
    }
    
    return { type: 'other', label: 'FILE', className: 'other-label' };
  };

  // Función para eliminar documento
  const handleDeleteDocument = (id) => {
    setDocumentos(prevDocs => prevDocs.filter(doc => doc.id !== id));
  };

  // Función para obtener el icono de estado
  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return '⏳';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '📄';
    }
  };

  // Componente de loading con puntos
  const DotLoading = () => (
    <div className="dot-loading">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );

  // Función para abrir modal
  const openModal = (doc) => {
    if (doc.status === 'success' && doc.analysis) {
      setSelectedDocument({
        archivoURL: URL.createObjectURL(doc.archivo),
        fileType: getFileTypeInfo(doc.archivo).type,
        fileName: doc.nombre
      });
      setIsModalOpen(true);
    }
  };

  // Función para cerrar modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  // Componente Modal
  const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>×</button>
          {children}
        </div>
      </div>
    );
  };

  // Componente para visualizar documentos
  const DocumentViewer = ({ archivo, fileType, fileName }) => {
    // Para archivos PDF, mostrar en iframe
    if (fileType === 'pdf') {
      return (
        <iframe
          src={archivo}
          className="pdf-viewer"
          title="PDF Viewer"
        />
      );
    }
    
    // Para otros tipos de archivo, mostrar mensaje genérico y botón de descarga
    else {
      return (
        <div className="generic-viewer-container">
          <div className="generic-preview-message">
            <h3>No se puede previsualizar</h3>
            <p>Este tipo de archivo no se puede previsualizar directamente.</p>
            <a 
              href={archivo} 
              download={fileName} 
              className="download-button"
            >
              Descargar archivo
            </a>
          </div>
        </div>
      );
    }
  };

  // Función para descargar archivos automáticamente
  const downloadFile = (url, filename) => {
    try {
      // Crear un elemento <a> temporal
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Agregar al DOM, hacer clic y remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`✅ Descarga iniciada: ${filename}`);
    } catch (error) {
      console.error('Error al iniciar descarga:', error);
      showNotification('Error al iniciar la descarga automática', 'error');
    }
  };

  return (
    <div>
      <TopBarHeader />
      <h1 className="h1-titulo-2">Splitter Documental</h1>

      {/* Notificación personalizada para SpliterDocumental */}
      {notification.show && (
        <div className={`splitter-notification splitter-notification-${notification.type}`}>
          <div className="splitter-notification-content">
            <div className="splitter-notification-icon">
              {notification.type === 'success' && '✅'}
              {notification.type === 'error' && '❌'}
              {notification.type === 'warning' && '⚠️'}
            </div>
            <div className="splitter-notification-message">
              {notification.message}
            </div>
            <button 
              type="button" 
              className="splitter-notification-close" 
              onClick={() => setNotification({...notification, show: false})}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="upload-container">
        {/* Uploader similar al de asistencia-libro */}
        <div className="upload-box" 
             onClick={handleButtonClick}
             onDragEnter={handleDrag}
             onDragOver={handleDrag}
             onDragLeave={handleDrag}
             onDrop={handleDrop}
        >
          <div className={`upload-area ${dragActive ? "drag-active" : ""}`}>
            <img src={icon} alt="Upload icon" className="upload-icon" />
            <p>Arrastra o sube tus archivos PDF. <span className="browse">Aquí</span></p>
            <p className="supports">Solo se permiten archivos PDF (.pdf)</p>
            <input 
              ref={inputRef}
              type="file" 
              accept=".pdf"
              multiple
              onChange={handleInputChange}
              className="file-input-hidden" 
            />
          </div>
        </div>
        
        {/* Mostrar documentos solo si hay documentos */}
        {documentos.length > 0 && (
          <div className="documentos-list">
            {documentos.map((doc) => {
              return (
                <div 
                  key={doc.id} 
                  className="documento-item"
                >
                  <div className="documento-icon-container">
                    {doc.archivo && (
                      <div className={getFileTypeInfo(doc.archivo).className}>
                        {getFileTypeInfo(doc.archivo).label}
                      </div>
                    )}
                    <div className="image-placeholder">
                      <div className="image-icon"></div>
                    </div>
                  </div>
                  <div className="documento-info">
                    <span className="status-icon">{getStatusIcon(doc.status)}</span>
                    
                    <div className="documento-nombre">
                      {doc.nombre}
                      {doc.pageCount && (
                        <span className="page-count-badge">
                          {doc.pageCount} página{doc.pageCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {doc.hasSeparatedPages && doc.zipDownloadUrl && (
                        <span className="zip-downloaded-badge" title="Páginas separadas descargadas automáticamente">
                          📦 ✅
                        </span>
                      )}
                    </div>
                    
                    {doc.status === 'uploading' ? (
                      <div className="loading-container">
                        <DotLoading />
                      </div>
                    ) : (
                      <div className="splitter-actions-container">
                        <button 
                          type="button"
                          className="splitter-action-btn splitter-view-btn" 
                          onClick={() => openModal(doc)}
                          disabled={doc.status === 'uploading'}
                          title="Ver documento"
                        >
                          <img src={eyeIcon} alt="Ver" className="splitter-icon" />
                        </button>
                        <button 
                          type="button"
                          className="splitter-action-btn splitter-delete-btn" 
                          onClick={() => handleDeleteDocument(doc.id)}
                          disabled={doc.status === 'uploading'}
                          title="Eliminar documento"
                        >
                          <img src={trashIcon} alt="Eliminar" className="splitter-icon" />
                        </button>
                      </div>
                    )}
                    
                    {doc.status === 'error' && (
                      <div className="error-message">
                        <small>Error: {doc.error}</small>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedDocument && (
          <DocumentViewer 
            archivo={selectedDocument.archivoURL} 
            fileType={selectedDocument.fileType}
            fileName={selectedDocument.fileName}
          />
        )}
      </Modal>
    </div>
  );
};

export default SpliterDocumental; 